import React, { FC, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Dialog, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as yup from 'yup';
import BankingService from 'services/banking.service';
import { useToasts } from 'hooks/toasts';
import { formatCurrency, parseCurrency } from 'utils/banking';
import { IAccount } from 'common/accounts.interface';
import { formatTaxNumber } from 'utils/formatters';
import { ICounterparty } from 'common/secondary-operation.interface';

export interface ManualWithdrawFormProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  accounts: IAccount[];
  counterparties: ICounterparty[];
  loadingFilters: boolean;
}

interface ManualWithdrawFormValues {
  originBankCode: string;
  originBankBranch: string;
  originBankAccount: string;
  destinationBankCode: string;
  destinationBankBranch: string;
  destinationBankAccount: string;
  type: string;
  counterpartyId?: number;
  amount: string;
  commentary: string;
}

const initialValues: ManualWithdrawFormValues = {
  originBankCode: '509',
  originBankBranch: '0001',
  originBankAccount: '',

  destinationBankCode: '',
  destinationBankBranch: '',
  destinationBankAccount: '',

  type: '',
  counterpartyId: undefined,
  amount: '',
  commentary: '',
};

const validation = yup.object({
  amount: yup.string().required('Este é um campo obrigatório'),
  originBankAccount: yup.string().matches(/^\d+$/, 'Use apenas números').required('Este é um campo obrigatório'),
  type: yup.string().required('Este é um campo obrigatório'),
  counterpartyId: yup.number().when('type', {
    is: 'COUNTERPARTY',
    then: (schema) => schema.required('Este é um campo obrigatório'),
    otherwise: (schema) => schema,
  }),
  commentary: yup.string().required('Este é um campo obrigatório'),
});

const ManualWithdrawForm: FC<ManualWithdrawFormProps> = ({ open, onClose, onSubmitted, accounts, counterparties, loadingFilters }) => {
  const toast = useToasts();
  const [submitting, setSubmitting] = useState(false);

  const bankingService = useMemo(() => new BankingService(), []);

  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(null);
  const [selectedCounterparty, setSelectedCounterparty] = useState<ICounterparty | null>(null);

  const onSubmit = async (values: ManualWithdrawFormValues, resetForm: () => void) => {
    try {
      setSubmitting(true);

      await bankingService.createWithdrawalRequest({
        originBankCode: values.originBankCode,
        originBankBranch: values.originBankBranch,
        originBankAccount: values.originBankAccount.replace('-', ''),
        type: values.type,
        amount: parseCurrency(values.amount),
        commentary: values.commentary,
        ...(values.type === 'COUNTERPARTY'
          ? {
              counterpartyId: Number(values.counterpartyId),
            }
          : {}),
      });

      resetForm();
      setSelectedAccount(null);
      setSelectedCounterparty(null);
      onClose();
      onSubmitted();
    } catch (error) {
      toast.triggerToast('Erro ao criar a transferência. Contate o suporte.', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '50vw',
          maxHeight: '90vh',
          borderRadius: '16px',
          overflowY: 'auto',
        },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validation}
        onSubmit={async (values, { resetForm }) => onSubmit(values, resetForm)}
      >
        {({ values, handleChange, setFieldValue, handleSubmit, errors, touched, setFieldTouched }) => (
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ p: 4, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" fontSize={20} fontWeight={600} textAlign="center">
              Incluir Transferência - Conta Recebimento
            </Typography>

            <TextField
              fullWidth
              variant="standard"
              label="Valor de transferência"
              placeholder="R$ 0,00"
              value={values.amount}
              onChange={(e) => setFieldValue('amount', formatCurrency(e.target.value))}
              onBlur={() => setFieldTouched('amount', true)}
              error={Boolean(touched.amount && errors.amount)}
              helperText={touched.amount && errors.amount ? String(errors.amount) : ''}
              sx={{ mt: 3 }}
              InputLabelProps={{ shrink: true }}
            />

            <Autocomplete
              value={selectedAccount}
              onChange={(_, newValue) => {
                setSelectedAccount(newValue);

                if (newValue) {
                  setFieldValue('originBankCode', newValue.bankCode);
                  setFieldValue('originBankBranch', newValue.bankBranch);
                  setFieldValue('originBankAccount', newValue.bankAccount);
                } else {
                  setFieldValue('originBankCode', '');
                  setFieldValue('originBankBranch', '');
                  setFieldValue('originBankAccount', '');
                }
              }}
              options={accounts}
              getOptionLabel={(option) => `${formatTaxNumber(option.cnpj)} - ${option.businessName}`}
              loading={loadingFilters}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Conta de origem" variant="standard" />}
              sx={{ mt: 3 }}
            />

            {selectedAccount && (
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField value={values.originBankCode} variant="standard" fullWidth disabled />
                <TextField value={values.originBankBranch} variant="standard" fullWidth disabled />
                <TextField value={values.originBankAccount} variant="standard" fullWidth disabled />
              </Box>
            )}

            <FormControl fullWidth variant="standard" sx={{ mt: 3 }}>
              <InputLabel shrink>Tipo de transferência</InputLabel>
              <Select
                value={values.type}
                onChange={(e) => {
                  const val = e.target.value as string;
                  setFieldValue('type', val);

                  if (val !== 'COUNTERPARTY') {
                    setFieldValue('counterpartyId', undefined);
                    setSelectedCounterparty(null);

                    setFieldValue('destinationBankCode', '');
                    setFieldValue('destinationBankBranch', '');
                    setFieldValue('destinationBankAccount', '');
                  }
                }}
              >
                <MenuItem value="" disabled>
                  Selecione
                </MenuItem>
                <MenuItem value="HOLDER">Titular</MenuItem>
                <MenuItem value="COUNTERPARTY">Financiador</MenuItem>
              </Select>
            </FormControl>

            {values.type === 'COUNTERPARTY' && (
              <>
                <Autocomplete
                  value={selectedCounterparty}
                  onChange={(_, newValue) => {
                    setSelectedCounterparty(newValue);

                    if (newValue) {
                      setFieldValue('counterpartyId', newValue.id);
                      setFieldValue('destinationBankCode', newValue.bankCode);
                      setFieldValue('destinationBankBranch', newValue.bankBranch);
                      setFieldValue('destinationBankAccount', newValue.bankAccount);
                    } else {
                      setFieldValue('counterpartyId', undefined);
                      setFieldValue('destinationBankCode', '');
                      setFieldValue('destinationBankBranch', '');
                      setFieldValue('destinationBankAccount', '');
                    }
                  }}
                  options={counterparties}
                  getOptionLabel={(option) => `${formatTaxNumber(option.documentNumber)} - ${option.companyName}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  loading={loadingFilters}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Financiador destinatário"
                      variant="standard"
                      error={Boolean(touched.counterpartyId && errors.counterpartyId)}
                      helperText={touched.counterpartyId && errors.counterpartyId ? 'Este é um campo obrigatório' : ''}
                    />
                  )}
                  sx={{ mt: 3 }}
                />

                {selectedCounterparty && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <TextField value={values.destinationBankCode} variant="standard" fullWidth disabled />
                    <TextField value={values.destinationBankBranch} variant="standard" fullWidth disabled />
                    <TextField value={values.destinationBankAccount} variant="standard" fullWidth disabled />
                  </Box>
                )}
              </>
            )}

            <TextField
              label="Comentário"
              name="commentary"
              value={values.commentary}
              onChange={handleChange}
              variant="standard"
              fullWidth
              sx={{ mt: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 5 }}>
              <Button fullWidth variant="outlined" onClick={onClose}>
                Cancelar
              </Button>
              <Button fullWidth type="submit" variant="contained" disabled={submitting}>
                Confirmar
              </Button>
            </Box>
          </Box>
        )}
      </Formik>
    </Dialog>
  );
};

export default ManualWithdrawForm;
